import { VocabularyTestSelectionItem, WordSetCategoryType } from 'app/types';

export type ScopeSelectionKey = string;

/** Key format: `${wordSetType}||${categoryName}`. */
export const buildScopeKey = (
  wordSetType: WordSetCategoryType,
  categoryName: string,
): ScopeSelectionKey => `${wordSetType}||${categoryName}`;

export const parseScopeKey = (
  key: ScopeSelectionKey,
): { wordSetType: WordSetCategoryType; categoryName: string } => {
  const [wordSetType, categoryName] = key.split('||');
  return { wordSetType: wordSetType as WordSetCategoryType, categoryName };
};

/** Selected subCategoryNames per `wordSetType||categoryName` key. */
export type ScopeSelectionMap = Record<ScopeSelectionKey, string[]>;

/** Every subcategory that exists per `wordSetType||categoryName` key, for
 * the categories that have been loaded so far. A selection is compared
 * against this to detect "the whole word set is selected". */
export type ScopeUniverseMap = Record<ScopeSelectionKey, string[]>;

export const countSelectedSubCategories = (
  selection: ScopeSelectionMap,
): number =>
  Object.values(selection).reduce((acc, names) => acc + names.length, 0);

export const countTouchedCategories = (selection: ScopeSelectionMap): number =>
  Object.values(selection).filter((names) => names.length > 0).length;

export const getTouchedWordSetTypes = (
  selection: ScopeSelectionMap,
): WordSetCategoryType[] => {
  const types = new Set<WordSetCategoryType>();
  Object.entries(selection).forEach(([key, names]) => {
    if (names.length) types.add(parseScopeKey(key).wordSetType);
  });
  return Array.from(types);
};

/**
 * A word set reads as "fully selected" when every one of the given
 * `categoryNames` has a matching, non-empty entry in `universe` whose
 * length equals the selected length for that category. A category missing
 * from `universe` (its subcategories were never fetched) counts as NOT
 * fully selected — callers can't confirm full coverage for data they don't
 * have yet, so this stays conservative rather than guessing.
 */
export const isWordSetFullySelected = (
  selection: ScopeSelectionMap,
  universe: ScopeUniverseMap,
  categoryNames: string[],
  wordSetType: WordSetCategoryType,
): boolean => {
  if (!categoryNames.length) return false;
  return categoryNames.every((categoryName) => {
    const key = buildScopeKey(wordSetType, categoryName);
    const total = universe[key];
    if (!total || !total.length) return false;
    const selected = selection[key] || [];
    return selected.length === total.length;
  });
};

export interface ScopeSummary {
  selectedCount: number;
  catCount: number;
  /** The single word set type that's fully selected, when the whole
   * selection amounts to "every category/subcategory of one list" and
   * nothing from any other list is touched. `null` otherwise. */
  soleFullWordSetType: WordSetCategoryType | null;
  /** True only when `soleFullWordSetType` equals the default word set. */
  isDefault: boolean;
}

export const computeScopeSummary = (
  selection: ScopeSelectionMap,
  universe: ScopeUniverseMap,
  categoryNamesByWordSetType: Record<string, string[]>,
  defaultWordSetType: WordSetCategoryType,
): ScopeSummary => {
  const selectedCount = countSelectedSubCategories(selection);
  const catCount = countTouchedCategories(selection);
  const touchedTypes = getTouchedWordSetTypes(selection);

  let soleFullWordSetType: WordSetCategoryType | null = null;
  if (touchedTypes.length === 1) {
    const [type] = touchedTypes;
    const categoryNames = categoryNamesByWordSetType[type] || [];
    if (isWordSetFullySelected(selection, universe, categoryNames, type)) {
      soleFullWordSetType = type;
    }
  }

  return {
    selectedCount,
    catCount,
    soleFullWordSetType,
    isDefault: soleFullWordSetType === defaultWordSetType,
  };
};

/** Builds a selection map with every subcategory in `universe` checked for
 * the named categories of `wordSetType` — used to seed the "whole default
 * list selected" starting state. Categories missing from `universe` are
 * silently skipped (their subcategories haven't loaded yet). */
export const buildFullSelectionFromUniverse = (
  universe: ScopeUniverseMap,
  categoryNames: string[],
  wordSetType: WordSetCategoryType,
): ScopeSelectionMap => {
  const result: ScopeSelectionMap = {};
  categoryNames.forEach((categoryName) => {
    const key = buildScopeKey(wordSetType, categoryName);
    if (universe[key]) result[key] = [...universe[key]];
  });
  return result;
};

/** Converts the internal selection map into the API payload shape. Empty
 * when nothing is selected — callers treat that as "no explicit scope". */
export const toSelectionItems = (
  selection: ScopeSelectionMap,
): VocabularyTestSelectionItem[] =>
  Object.entries(selection)
    .filter(([, subCategoryNames]) => subCategoryNames.length)
    .map(([key, subCategoryNames]) => {
      const { wordSetType, categoryName } = parseScopeKey(key);
      return { wordSetType, categoryName, subCategoryNames };
    });
